/* Copyright (c) 2020-present CloudIO Inc */

import { EMPTY_PAGE } from 'root/Resource';
import { getCustomChildItemTypes } from '../../registry/Manager';
import { checkForFixedChild } from '../../utils/ManagerUtils';
import { Platform } from '../../platform/Platform';
import {
  BaseComponentItem,
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './Page';
import { analyzePage } from './PageAnalyze';
import { helpRenders, twhelpRenders } from './Page_Help';

export function sortItem(
  aItem: BaseComponentItem,
  bItem: BaseComponentItem,
  itemTypes: ItemType[],
) {
  for (let i = 0; i < itemTypes.length; i++) {
    const itemType = itemTypes[i];
    if (aItem.type === itemType && bItem.type === itemType) {
      return 0;
    }
    if (aItem.type === itemType) {
      return -1;
    }
    if (bItem.type === itemType) {
      return 1;
    }
  }
  return 0;
}

const PageManager: ComponentManagerFn<'Page'> = (
  platform: Platform,
): ComponentManager<'Page'> => {
  return {
    ...baseComponentManager<'Page'>(platform, 'Page'),
    defaultValue,
    isItemIdReadOnly: () => true,
    isChildAllowed: (page, item, childType, context) =>
      (
        [
          ...getCustomChildItemTypes('Page'),
          'AttachmentRevisions',
          'BoxContainer',
          'Card',
          'Carousel',
          'CloudDataStore',
          'Comments',
          'DataStore',
          'DataStoreReference',
          'Drawer',
          'EmbedPage',
          'EventSubscription',
          'ExpressionWatcher',
          'FAB',
          'FlexPanel',
          'FloatingPanel',
          'Folder',
          'HorizontalSplitPanel',
          'Interval',
          'LocalDataStore',
          'PageHeader',
          'Paper',
          'Popup',
          'PulseTip',
          'ReadOnlyDataStore',
          'RowPanel',
          'SidebarPanel',
          'SignaturePad',
          'SpeedDial',
          'SuspendPage',
          'TabPanel',
          'Transaction',
          'TransientDataStore',
          'TriggerOnExtraLargeScreen',
          'TriggerOnExtraSmallScreen',
          'TriggerOnLandscapeScreen',
          'TriggerOnLargeScreen',
          'TriggerOnMediumScreen',
          'TriggerOnPageActive',
          'TriggerOnPageExit',
          'TriggerOnPortraitScreen',
          'TriggerOnSmallScreen',
          'TriggerOnWindowSizeChange',
          'VerticalSplitPanel',
          'ChatBot',
        ] as ItemType[]
      ).includes(childType) &&
      checkForFixedChild(platform, page, item, childType, 'Page', context),
    props: {
      windowTitle: {
        label: 'Page Title',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'This will be also be set as the browser window title.',
      },
      padding: {
        label: 'Padding',
        type: 'padding',
        category: 'Spacing',
      },
      backgroundColor: {
        label: 'BG Color',
        type: 'BGColor',
        category: 'Look & Feel',
        allowCustomColor: false,
      },
      border: {
        label: 'Border',
        type: 'border',
        category: 'Look & Feel',
      },
      onPageActiveFunction: {
        label: 'On Page Active',
        type: 'controllerFunction',
        category: 'Controllers',
      },
      onPageExitFunction: {
        label: 'On Page Exit',
        type: 'controllerFunction',
        category: 'Controllers',
      },
      minHeight: {
        label: 'Min Height',
        type: 'number',
        min: 600,
        max: 2000,
        step: 40,
        helpText:
          "Provide the height in pixels, if your page design requires a minimum height to properly render. A vertical scrollbar will show up if the browser window's inner height is less than this value.",
      },
      minWidth: {
        label: 'Min Width',
        type: 'number',
        min: 600,
        max: 2000,
        step: 40,
        helpText:
          "Provide the width in pixels, if your page design requires a minimum width to properly render. A horizontal scrollbar will show up if the browser window's inner width is less than this value.",
      },
      floatingHeader: {
        label: 'Floating Header',
        type: 'boolean',
      },
      withBootstrap: {
        label: 'With Bootstrap',
        type: 'boolean',
        helpText:
          'Enable this if you want to use Bootstrap CSS classes. Bootstrap CSS classes are scoped within `.bs-css-root`, hence need to add the class `bs-css-root` either to the Page or to the topmost parent in which the Bootstrap CSS classes being used.',
      },
      withTailwind: {
        label: 'With Tailwind',
        type: 'boolean',
        helpText:
          'Enable this if you want to use Tailwind CSS classes. Tailwind CSS classes are scoped within `.tw-css-root`, hence need to add the class `tw-css-root` either to the Page or to the topmost parent in which the Tailwind CSS classes being used.',
      },
    },
    canHaveChildren: () => true,
    category: 'Containers',
    layout: 'sizeAware',
    onChildMoveIn: ({ page, value }) => {
      // DataStore must be on top before any other item type
      value.children = value.children.sort((a, b) => {
        const aItem = page[a];
        const bItem = page[b];
        if (aItem && bItem) {
          return sortItem(aItem, bItem, [
            'Folder',
            'CloudDataStore',
            'DataStore',
            'LocalDataStore',
            'ReadOnlyDataStore',
            'TransientDataStore',
            'Transaction',
            'Comments',
            'Drawer',
            'ExpressionWatcher',
            'Popup',
            'TriggerOnExtraLargeScreen',
            'TriggerOnExtraSmallScreen',
            'TriggerOnLandscapeScreen',
            'TriggerOnLargeScreen',
            'TriggerOnMediumScreen',
            'TriggerOnPageActive',
            'TriggerOnPageExit',
            'TriggerOnPortraitScreen',
            'TriggerOnSmallScreen',
            'TriggerOnWindowSizeChange',
            'PageHeader',
            'SidebarPanel',
            'FloatingPanel',
            'SpeedDial',
            'FAB',
            'ChatBot',
          ]);
        }
        return 0;
      });
    },
    icon: { iconName: 'desktop' },
    getDisplayText: (item) => item.props.windowTitle.value,
    analyze: analyzePage,
    excludeChildren: true, // let the Page.tsx handle the children after initializing the stores
    description: `**Overview**<br/>
The Page is the root-level container that represents a single screen or view within your application. It serves as the canvas upon which all other components—layouts, data stores, and visual elements—are placed.
Beyond acting as a wrapper, the Page component manages the global lifecycle, window properties, and responsive behavior of the interface. Every interface you build starts here.

**1. Global Configuration**<br/>
These settings control the high-level attributes of the browser window and the page's structural foundation.
* **Page Title:** Sets the text displayed in the browser tab. This property supports dynamic expressions, allowing you to update the title based on context (e.g., "Customer Profile - John Doe").
* **Floating Header:** If enabled, the Page Header (if added) will remain fixed at the top of the viewport while the rest of the page content scrolls beneath it.
* **Background Color:** Defines the base color of the entire screen.
* **Padding:** Controls the global spacing around the edges of the page content.
* **Border:** Adds a boundary line around the page container.

**2. Responsiveness & Scrolling**<br/>
Use these properties to enforce layout constraints, ensuring your design holds its structure even on varying screen sizes.
* **Min Height:** Defines the minimum vertical size (in pixels) required for the page design. If the browser window is shorter than this value, a vertical scrollbar will appear automatically.
* **Min Width:** Defines the minimum horizontal size (in pixels). If the browser window is narrower than this value, a horizontal scrollbar will appear.

**3. Lifecycle Controllers**<br/>
The Page acts as the "Brain" of the screen, offering hooks to execute logic when the user enters or leaves the view.
* **On Page Active:** A controller function triggered immediately when the page loads or becomes the active tab. Use this to initialize variables, fetch data, or start timers.
* **On Page Exit:** A controller function triggered just before the user navigates away. Use this to save state, clear intervals, or reset temporary data.

**4. CSS Framework Integration**<br/>
The Page component includes built-in support for popular CSS frameworks, managing the scoping automatically so you can use utility classes on child components.
* **With Bootstrap:** Enables Bootstrap support. (Technical Note: Scopes styles within the .bs-css-root class).
* **With Tailwind:** Enables Tailwind CSS support. (Technical Note: Scopes styles within the .tw-css-root class).

**5. Infrastructure Management**<br/>
The Page is the only component capable of hosting "Infrastructure" items. To keep your project organized, the Page automatically sorts its children into a strict hierarchy:
* **Infrastructure:** Folders, Data Stores (Cloud/Local), and Transactions are sorted to the top.
* **Overlays:** Popups, Drawers, and Floating Panels are managed next.
* **Logic:** Triggers and Expression Watchers.
* **UI Layout:** Visual headers, panels, and content appear last.`,
    helpRenders: [
      {
        title: 'Page',
        description: 'Sample page with min width & hight set to large values.',
        page: EMPTY_PAGE,
        exampleId: 'e1',
        height: 280,
        visibleProps: ['windowTitle', 'minHeight', 'minWidth'],
        item: {
          children: [],
          itemId: 'root',
          parent: 'root',
          props: {
            windowTitle: {
              type: 'text',
              value: 'Check this browser tab title!',
            },
            backgroundColor: 'default',
            minHeight: 1800,
            minWidth: 2400,
          },
          type: 'Page',
        },
      },
      ...helpRenders,
      ...twhelpRenders,
    ],
  };
};
export default PageManager;
